import { Modal, notification } from "antd";
import cuid from "cuid";
import { defaults, filter, get, keyBy, last, map, sortBy } from "lodash";
import { useEffect, useState } from "react";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import EmailGreen from "shared/components/SVG/LMF/EmailGreen";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import {
  GENERATE_QUOTE_DOCUMENTS,
  GENERATE_ZIP_DOCUMENT,
} from "utils/api/graphql/mutations/attachment";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { UPDATE_PROJECT_GRA } from "utils/api/graphql/mutations/projects";
import { SEND_PERSONALIZED_QUOTE } from "utils/api/graphql/mutations/webhook";
import { GENERAL_COST } from "utils/api/graphql/queries/general-cost";
import {
  CONTACT_POINT_SYSTEM,
  KEYS,
  SUR_COMPLEMENT_WEIGHT,
} from "utils/constants";
import { v4 } from "uuid";
import { filterByConditions } from "../../../..";
import Template from "../Template";
import View from "./View";

const AdherentDetails = ({
  project,
  tracer,
  currentStatus,
  onNext,
  processId,
  onBack,
  setDisplayProject,
}) => {
  const [sendPersonalizedQuote] = useMutation(SEND_PERSONALIZED_QUOTE);
  const [updateProjectGRA] = useMutation(UPDATE_PROJECT_GRA, {
    variables: { where: { id: project.id } },
  });
  const [addLink] = useMutation(CREATE_LINK);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [generateZipDocuments] = useMutation(GENERATE_ZIP_DOCUMENT);
  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const [getSubscription, { data }] = useLazyQuery(
    graphql.queries.SUBSCRIPTION,
  );

  const [getGeneralCost, { data: generalCost }] = useLazyQuery(GENERAL_COST, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (subscriptionId)
      getSubscription({
        variables: { where: { id: subscriptionId } },
      });
  }, []);

  useEffect(() => {
    if (
      get(data, "subscription.insurancePlan.id") &&
      get(data, "subscription.project.id")
    ) {
      getGeneralCost({
        variables: {
          where: {
            project: { id: get(data, "subscription.project.id") },
            insurancePlan: { id: get(data, "subscription.insurancePlan.id") },
          },
        },
      });
    }
  }, [data]);

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
  });

  const next = ({ contact, contract }) => {
    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );
    const mappedTelecoms = map(get(contact, "telecoms"), (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );

    setSuccessModalVisible(true);
    setTimeout(() => {
      setSuccessModalVisible(false);
      onNext({
        payload: {
          visible: true,
          locked: true,
          contact: {
            ...contact,
            telecoms: mappedTelecoms,
          },
          contract,
        },
        onCompleted: () => {
          const selectedUpsells = get(
            project,
            `fields.upsells.${get(data, "subscription.insurancePlan.id")}`,
            [],
          );
          updateProjectGRA({
            variables: {
              where: { id: project.id },
            },
            onCompleted: ({ updateProjectGRA }) => {
              setDisplayProject((prev) => ({
                ...prev,
                fields: {
                  ...get(prev, "fields", {}),
                  graCode: get(updateProjectGRA, "fields.graCode"),
                },
              }));
              generateQuoteDocuments({
                variables: {
                  data: {
                    project: { id: project.id },
                    insurancePlans: [
                      { id: get(data, "subscription.insurancePlan.id") },
                    ],
                  },
                },
                onCompleted: () => {
                  const source = localStorage.getItem(KEYS.SOURCE);
                  addLink({
                    variables: {
                      data: {
                        url: get(
                          data,
                          "subscription.insurancePlan.config.generatedUrl",
                          `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                        ),
                        project: { id: project.id },
                        subscription: { id: subscriptionId },
                        fields: {
                          process: { id: processId },
                          status: { id: get(possibleStatus, "nextStatus") },
                        },
                      },
                    },
                    onCompleted: async ({ addLink }) => {
                      const mappedTelecoms = map(
                        get(contact, "telecoms"),
                        (item, key) =>
                          defaults(item, { id: cuid(), system: key }),
                      );
                      const telecomsMap = keyBy(mappedTelecoms, "system");

                      const email = get(telecomsMap, "EMAIL.value");
                      const firstName = get(contact, "user.firstname");
                      const lastName = get(contact, "user.lastname");

                      if (!email || !firstName || !lastName) {
                        notification.error({
                          message: "Impossible d'envoyer le webhook",
                          description: `Données manquantes : ${
                            !firstName ? "prénom, " : ""
                          }${!lastName ? "nom, " : ""}${!email ? "email" : ""}`,
                        });
                        return;
                      }

                      const basePrice = get(generalCost, "generalCost.cost", 0);
                      const upsellsPrice = selectedUpsells.reduce(
                        (acc, { cost }) => acc + cost,
                        0,
                      );
                      const totalPrice = basePrice + upsellsPrice;

                      const mainPlanName = get(
                        data,
                        "subscription.insurancePlan.name",
                      );
                      const selectedModulesText =
                        selectedUpsells.length > 0
                          ? ` + ${selectedUpsells
                              .map(({ insurancePlan }) => insurancePlan.name)
                              .join(", ")}`
                          : "";
                      const fullLabel = `${mainPlanName} / ${selectedModulesText}`;

                      const surComplementPlanRefs = filter(selectedUpsells, {
                        weight: SUR_COMPLEMENT_WEIGHT,
                      }).map(({ insurancePlan: { id } }) => id);

                      const {
                        data: {
                          generateZipDocuments: { fileUrl: documentsUrl },
                        },
                      } = await generateZipDocuments({
                        variables: {
                          where: {
                            OR: [
                              ...surComplementPlanRefs.map((id) => ({
                                AND: [
                                  { insurancePlans: { id } },
                                  { project: null },
                                ],
                              })),
                              {
                                AND: [
                                  {
                                    insurancePlans: {
                                      id: get(
                                        data,
                                        "subscription.insurancePlan.id",
                                      ),
                                    },
                                  },
                                  {
                                    OR: [
                                      { project: { id: project.id } },
                                      { project: null },
                                    ],
                                  },
                                ],
                              },
                            ],
                          },
                        },
                      });

                      const products = [
                        {
                          label: fullLabel,
                          price:
                            totalPrice !== undefined
                              ? `${totalPrice.toFixed(2)}€/mois`
                              : "",
                          url: get(
                            data,
                            "subscription.insurancePlan.config.generatedUrl",
                            `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}?l=${addLink.id}`,
                          ),
                          documentsUrl,
                        },
                      ];

                      sendPersonalizedQuote({
                        variables: {
                          notification: {
                            recipient: email,
                            externalId: v4(),
                            language: "FR",
                            data: {
                              lastName: lastName,
                              firstName: firstName,
                              products: products,
                              agentFirstName: get(
                                project,
                                "author.firstname",
                                "Camille",
                              ),
                              agentEmail: get(
                                project,
                                "author.email",
                                "camille@mutuelle-familiale.fr",
                              ),
                            },
                          },
                        },
                        onCompleted: () => {
                          notification.open({
                            message: "Devis envoyé",
                            duration: 5,
                            icon: (
                              <IoCheckmarkCircleOutline
                                color={COLORS.C_SUCCESS}
                              />
                            ),
                          });
                        },
                        onError: (err) => {
                          notification.error({
                            message: "Erreur webhook Engage",
                            description: err.message,
                          });
                        },
                      });
                    },
                  });
                },
              });
            },
          });
        },
      });
    }, 2000);
  };

  const initialValues = {
    contact: {
      ...get(project, "contact", {}),
      telecoms,
      acceptDigital: get(project, "contact.acceptDigital", false),
      acceptContact: get(project, "contact.acceptContact", false),
      fields: {
        ...get(project, "contact.fields", {}),
      },
    },
    contract: get(project, "contract"),
  };

  return (
    <>
      <Modal
        open={successModalVisible}
        footer={null}
        centered
        closable={false}
        className="lmf__modal"
        width={400}
      >
        <div className="lmf__modal--content lmf__modal--center">
          <EmailGreen />
          <h3 className="message">Un devis vous a été envoyé.</h3>
        </div>
      </Modal>
      <Template
        onBack={onBack}
        progress={currentStatus.progress}
        tracer={tracer}
        subscriptionId={subscriptionId}
        // project={project}
        HeaderComponent={{
          subscription: get(data, "subscription"),
          tracer,
          project,
        }}
        View={
          <View
            onNext={next}
            initialValues={initialValues}
            fields={get(project, "fields", {})}
            locked={project.locked}
            disabled={project?.locked}
            onBack={onBack}
          />
        }
      />
    </>
  );
};

export default AdherentDetails;
